import React from 'react';
// Valuation layer is USD — it must match the signed ASA cap and the deck.
const SYM = '$';

/**
 * RoundsPlanCard — renders the two-step phased capital path.
 *
 * Backend supplies a `rounds_plan` array (one entry per phase) with both
 * the raw structural inputs (target_raise_usd, valuation_basis_usd,
 * basis_label, tagline, status) AND the live-derived outputs
 * (base_share_price_usd, dilution_pct, new_shares_issued, post_money_usd).
 *
 * The same component is used inside the public /investors block and the
 * internal Cap Table tab so pricing/dilution labels never drift.
 *
 * Props:
 *   plan      — array of phase objects from `rounds_plan` / `rounds_plan_live`
 *   variant   — 'public' (warm pastel on dark) | 'admin' (white-card)
 */

const fmtMoney = (n) => {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000_000) return `${SYM}${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `${SYM}${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${SYM}${(n / 1_000).toFixed(0)}k`;
  return `${SYM}${Number(n).toLocaleString()}`;
};

const fmtMoneyFull = (n) => (n === null || n === undefined ? '—' : `${SYM}${Number(n).toLocaleString()}`);
const fmtInt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString());

const STATUS_LABELS = {
  open: { label: 'Open Round', dot: '#0D5E4E' },
  modeled: { label: 'Modeled · Series A', dot: '#1D4ED8' },
  closed: { label: 'Closed', dot: '#475569' },
};

export const RoundsPlanCard = ({ plan, variant = 'public' }) => {
  const phases = Array.isArray(plan) ? plan : [];
  if (phases.length === 0) return null;

  const isPublic = variant === 'public';

  // Both variants now render on a light background (the dark-themed /investors
  // page was retired in Jun 2026). `public` keeps a soft peach/lavender card
  // tint to feel warmer next to the marketing copy, while `admin` is pure
  // white for the data-room context.

  return (
    <section
      data-testid={`rounds-plan-${variant}`}
      className="rounded-3xl p-6 lg:p-8"
      style={{
        background: isPublic ? 'rgba(255, 255, 255, 0.70)' : '#fff',
        border: '1px solid rgba(92,82,106,0.10)',
        color: '#0B1528',
      }}
    >
      <div className="flex items-end justify-between gap-3 mb-5">
        <div>
          <div
            className="text-[10px] uppercase tracking-[0.18em] font-bold mb-1"
            style={{ color: '#9A4A33' }}
          >
            Two-Step Capital Path
          </div>
          <h3 className="text-xl lg:text-2xl font-black tracking-tight" style={{ color: '#0B1528' }}>
            Phased fundraising — founder control preserved
          </h3>
          <p className="text-sm mt-1" style={{ color: '#475569' }}>
            Pricing and dilution are derived live from the canonical cap-table doc — never hardcoded.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {phases.map((p, idx) => {
          const status = STATUS_LABELS[p.status] || { label: p.status || '—', dot: '#475569' };
          const isPhase1 = idx === 0;
          return (
            <article
              key={p.key || idx}
              data-testid={`rounds-plan-phase-${p.key || idx}`}
              className="rounded-2xl p-5 lg:p-6 transition-transform hover:scale-[1.005]"
              style={{
                background: isPhase1 ? '#F8FAFC' : '#F0F4FB',
                border: `1px solid ${isPhase1 ? 'rgba(10,37,64,0.45)' : 'rgba(155,184,237,0.45)'}`,
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] font-bold" style={{ color: '#475569' }}>
                  Phase {p.phase || idx + 1} · {p.round_label || p.name}
                </div>
                <span className="text-[10px] font-bold flex items-center gap-1.5" style={{ color: '#0B1528' }} data-testid={`rounds-plan-status-${p.key || idx}`}>
                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: status.dot }} /> {status.label}
                </span>
              </div>

              <h4 className="text-2xl lg:text-3xl font-black tracking-tight" style={{ color: '#0B1528' }}>{p.name}</h4>
              {p.tagline && (
                <p className="text-sm mt-1 mb-4" style={{ color: '#475569' }}>
                  {p.tagline}
                </p>
              )}

              <div className="grid grid-cols-2 gap-3">
                <KPI label="Target Raise" value={fmtMoney(p.target_raise_usd)} testid={`raise-${p.key || idx}`} accent={isPhase1 ? '#C45A33' : '#3D5FA8'} />
                <KPI label="Valuation Basis" value={fmtMoney(p.valuation_basis_usd)} sub={p.basis_label ? String(p.basis_label).replace(/\$/g, SYM) : p.basis_label} testid={`basis-${p.key || idx}`} />
                <KPI label="Base Share Price" value={`${SYM}${(p.base_share_price_usd || 0).toFixed(2)}`} testid={`price-${p.key || idx}`} accent={isPhase1 ? '#C45A33' : '#3D5FA8'} />
                <KPI label="Founder Dilution" value={`${(p.dilution_pct || 0).toFixed(2)}%`} testid={`dilution-${p.key || idx}`} />
                <KPI label="New Shares Issued" value={fmtInt(p.new_shares_issued)} testid={`shares-${p.key || idx}`} />
                <KPI label="Post-Money" value={fmtMoneyFull(p.post_money_usd)} testid={`postmoney-${p.key || idx}`} />
              </div>
            </article>
          );
        })}
      </div>

      <p className="mt-5 text-xs" style={{ color: '#475569' }}>
        Live-calculated against canonical cap-table inputs. Re-pricing the floor or total shares reshapes both phases simultaneously.
      </p>
    </section>
  );
};

const KPI = ({ label, value, sub, testid, accent }) => {
  return (
    <div data-testid={`rounds-plan-kpi-${testid}`}>
      <div className="text-[10px] uppercase tracking-[0.14em] font-bold mb-1" style={{ color: '#475569' }}>
        {label}
      </div>
      <div className="text-lg lg:text-xl font-black tabular-nums" style={{ color: accent || '#0B1528' }}>
        {value}
      </div>
      {sub && (
        <div className="text-[10px] mt-0.5" style={{ color: '#475569' }}>
          {sub}
        </div>
      )}
    </div>
  );
};

export default RoundsPlanCard;
